import {
  featureFlagsQuery,
  isFeatureEnabled,
  useFeatureFlag,
} from "@common/api/feature-flags"
import {
  quoteGroupPages,
  quoteOverviewQuery,
  quotesInfiniteQuery,
} from "@common/api/quoting/quote"
import { ensureListData } from "@common/api/utils"
import { DASHBOARD_QUOTE } from "@common/flags"
import type { QueryClient } from "@tanstack/react-query"
import { createFileRoute, Outlet, useNavigate } from "@tanstack/react-router"
import { ActiveFilterBar } from "@/components/layout/active-filter-bar"
import { ColumnChips } from "@/components/layout/column-chips"
import {
  type Display,
  DisplayOptions,
} from "@/components/layout/display-options"
import { FilterButton } from "@/components/layout/filter-button"
import { ListPage } from "@/components/layout/list-page"
import { ListTabs } from "@/components/layout/list-tabs"
import { ListToolbar } from "@/components/layout/list-toolbar"
import { NameFilter } from "@/components/layout/name-filter"
import { useColumnSizing } from "@/hooks/use-column-layout"
import { useWidth } from "@/hooks/use-width"
import { fitColumns } from "@/lib/column-widths"
import { requirePermission } from "@/lib/require-permission"
import { NewQuoteButton } from "@/routes/_layout/quoting_/quotes/-components/new-quote-menu"
import { QuoteOverview } from "./-components/overview/quote-overview"
import { QuoteTable } from "./-components/quote-table"
import { ReviewStrip } from "./-components/review-strip"
import {
  COLUMN_LABELS,
  COLUMN_SIZING,
  COLUMNS,
  type Column,
  DEFAULT_COLUMNS,
} from "./-lib/columns"
import {
  hasQuoteFilters,
  NO_OVERVIEW_FILTERS,
  NO_QUOTE_FILTERS,
  overviewFilterValues,
  type QuoteFilterValues,
  useQuoteListFilterConfigs,
  useQuoteOverviewFilterConfigs,
} from "./-lib/filters"
import { GROUPING_LABELS, GROUPINGS, type Grouping } from "./-lib/grouping"
import {
  displayOf,
  type QuotesDisplay,
  type QuotesSearch,
  quoteOverviewParams,
  quotePagesParams,
  quotesSearchSchema,
  TABS,
  type Tab,
  tabLabel,
} from "./-lib/search"

export const Route = createFileRoute("/_layout/quotes")({
  staticData: { breadcrumb: "Quotes" },
  validateSearch: (search) => quotesSearchSchema.parse(search),
  loaderDeps: ({ search }) => ({
    pages: quotePagesParams(search),
    grouped: Boolean(displayOf(search).group),
    overview:
      search.tab === "overview" ? quoteOverviewParams(search) : undefined,
  }),
  beforeLoad: async ({ context: { queryClient } }) => {
    await requirePermission(queryClient, "quote:read", "/")
  },
  loader: async ({ context: { queryClient }, deps }) => {
    void queryClient.prefetchQuery(featureFlagsQuery())
    if (deps.overview && (await overviewEnabled(queryClient))) {
      return queryClient.ensureQueryData(quoteOverviewQuery(deps.overview))
    }
    return ensureListData({
      grouped: deps.grouped,
      groups: () =>
        queryClient.ensureQueryData(quoteGroupPages.groups(deps.pages)),
      flat: () =>
        queryClient.ensureInfiniteQueryData(quotesInfiniteQuery(deps.pages)),
    })
  },
  component: QuotesPage,
})

async function overviewEnabled(queryClient: QueryClient): Promise<boolean> {
  const flags = await queryClient.ensureQueryData(featureFlagsQuery())
  return isFeatureEnabled(flags, DASHBOARD_QUOTE)
}

function useUpdateSearch(): (patch: Partial<QuotesSearch>) => void {
  const navigate = useNavigate()
  return (patch) =>
    navigate({
      to: ".",
      search: (previous) => ({ ...previous, ...patch }),
      replace: true,
    })
}

const LIST_SEARCH: Partial<QuotesSearch> = { months: undefined, end: undefined }

function QuotesPage(): React.ReactElement {
  const search = Route.useSearch()
  const overview = useFeatureFlag(DASHBOARD_QUOTE) ?? false
  const tabs = TABS.filter((option) => option !== "overview" || overview)
  const requested = search.tab ?? "all"
  const tab = tabs.includes(requested) ? requested : "all"
  const display = displayOf(search)
  const update = useUpdateSearch()
  const [ref, width] = useWidth<HTMLDivElement>()
  const sizing = useColumnSizing(display.columns, COLUMN_SIZING)
  const fit = fitColumns(display.columns, width, sizing)

  return (
    <ListPage
      toolbar={
        <Toolbar tabs={tabs} tab={tab} display={display} hidden={fit.hidden} />
      }
      strip={tab !== "overview" && tab !== "archived" && <ReviewStrip />}
      detail={<Outlet />}
      scrollRef={ref}
    >
      {tab === "overview" ? (
        <>
          <ActiveFilters tab={tab} />
          <QuoteOverview />
        </>
      ) : (
        <>
          <ActiveFilters tab={tab} />
          <QuoteTable
            search={search}
            display={display}
            columns={fit.shown}
            onSort={(column) => update(sortSearch(display, column))}
            onClear={() => update({ ...NO_QUOTE_FILTERS, search: undefined })}
          />
        </>
      )}
    </ListPage>
  )
}

function Toolbar({
  tabs,
  tab,
  display,
  hidden,
}: {
  tabs: Tab[]
  tab: Tab
  display: QuotesDisplay
  hidden: Column[]
}): React.ReactElement {
  const values = Route.useSearch()
  const { search = "" } = values
  const update = useUpdateSearch()
  const configs = useQuoteListFilterConfigs()
  const overviewConfigs = useQuoteOverviewFilterConfigs()
  const tabBar = (
    <ListTabs
      tabs={tabs}
      tab={tab}
      label={tabLabel}
      onSelect={(next) =>
        update(
          next === "overview"
            ? { tab: next }
            : { ...LIST_SEARCH, tab: next === "all" ? undefined : next },
        )
      }
    />
  )

  if (tab === "overview") {
    return (
      <ListToolbar
        tabs={tabBar}
        actions={
          <>
            <FilterButton
              active={hasQuoteFilters(overviewFilterValues(values))}
              configs={overviewConfigs}
              onChange={update}
            />
            <NewQuoteButton />
          </>
        }
      />
    )
  }

  return (
    <ListToolbar
      tabs={tabBar}
      actions={
        <>
          <NameFilter
            search={search}
            onSearch={(next) => update({ search: next || undefined })}
            placeholder="Search quotes…"
          />
          <FilterButton
            active={hasQuoteFilters(values)}
            configs={configs}
            onChange={update}
          />
          <DisplayOptions
            groupings={GROUPINGS}
            labels={GROUPING_LABELS}
            group={display.group}
            subgroup={display.subgroup}
            onChange={(next) => update(groupingSearch(next))}
          >
            <ColumnChips
              options={COLUMNS}
              labels={COLUMN_LABELS}
              fixed={["number"]}
              columns={display.columns}
              hidden={hidden}
              onChange={(next) => update(columnsSearch(next))}
            />
          </DisplayOptions>
          <NewQuoteButton />
        </>
      }
    />
  )
}

function groupingSearch({
  group,
  subgroup,
}: Display<Grouping>): Partial<QuotesSearch> {
  return {
    layout: undefined,
    group: group === "status" ? undefined : (group ?? "none"),
    subgroup,
  }
}

function columnsSearch(columns: Column[]): Partial<QuotesSearch> {
  const defaults =
    columns.length === DEFAULT_COLUMNS.length &&
    columns.every((column) => DEFAULT_COLUMNS.includes(column))
  return { columns: defaults ? undefined : columns }
}

function sortSearch(
  display: QuotesDisplay,
  column: Column,
): Partial<QuotesSearch> {
  const direction =
    column === display.sort && display.direction === "asc" ? "desc" : "asc"
  const byDefault = column === "updated" && direction === "desc"
  return byDefault
    ? { sort: undefined, direction: undefined }
    : { sort: column, direction }
}

function ActiveFilters({ tab }: { tab: Tab }): React.ReactElement {
  const {
    status,
    outcome,
    customer,
    aircraft,
    model,
    part,
    department,
    rep,
    creator,
    revision,
    period,
    from,
    to,
  } = Route.useSearch()
  const update = useUpdateSearch()
  const overview = tab === "overview"
  const listConfigs = useQuoteListFilterConfigs()
  const overviewConfigs = useQuoteOverviewFilterConfigs()
  const configs = overview ? overviewConfigs : listConfigs
  const listValues: QuoteFilterValues = {
    status,
    outcome,
    customer,
    aircraft,
    model,
    part,
    department,
    rep,
    creator,
    revision,
    period,
    from,
    to,
  }
  const values = overview ? overviewFilterValues(listValues) : listValues

  return (
    <ActiveFilterBar
      active={hasQuoteFilters(values)}
      configs={configs}
      values={values}
      onChange={update}
      onClear={() => update(overview ? NO_OVERVIEW_FILTERS : NO_QUOTE_FILTERS)}
    />
  )
}
